import { cookieStorage } from '@muse-web/storages'
import { webLogger } from '@muse-web/logger'

import { SESSION_ID_KEY, SESSION_TTL_SECONDS } from '../constants/cookieKeys'

const USABLE_ID = /^[1-9]\d*$/
const MAX_RANDOM_SUFFIX = 1000

// No `domain`: cookieStorage defaults to `.hostname`, which would sit next to the server's host-only cookie.
const COOKIE_PARAMS = { path: '/', secure: true, sameSite: 'none' } as const

type TRotationListener = (sessionId: string) => void

const rotationListeners = new Set<TRotationListener>()

let fallback: { id: string; touchedAt: number } | undefined

const mintSessionId = () =>
    String(Date.now() * 1000 + Math.floor(Math.random() * MAX_RANDOM_SUFFIX))

const writeSessionId = (sessionId: string) => {
    cookieStorage.set(SESSION_ID_KEY, sessionId, {
        expires: new Date(Date.now() + SESSION_TTL_SECONDS * 1000),
        params: COOKIE_PARAMS,
    })
}

export const onSessionRotation = (listener: TRotationListener) => {
    rotationListeners.add(listener)

    return () => {
        rotationListeners.delete(listener)
    }
}

const readStoredSessionId = () => {
    const stored = cookieStorage.get(SESSION_ID_KEY)

    return stored !== undefined && USABLE_ID.test(stored) ? stored : undefined
}

const liveFallbackId = () =>
    fallback !== undefined && Date.now() - fallback.touchedAt <= SESSION_TTL_SECONDS * 1000
        ? fallback.id
        : undefined

const notifyRotation = (sessionId: string) => {
    rotationListeners.forEach(listener => {
        try {
            listener(sessionId)
        } catch (error) {
            webLogger.error(error instanceof Error ? error : String(error))
        }
    })
}

export const touchSession = (): string => {
    const stored = readStoredSessionId()

    if (stored !== undefined) {
        fallback = undefined
        writeSessionId(stored)

        return stored
    }

    const minted = liveFallbackId() ?? mintSessionId()

    writeSessionId(minted)

    const current = readStoredSessionId()

    if (current === undefined) {
        fallback = { id: minted, touchedAt: Date.now() }

        return minted
    }

    fallback = undefined

    if (current !== minted) {
        return current
    }

    notifyRotation(minted)

    return minted
}

const MS_PER_ID_TICK = 1000

const isMintedAfter = (sessionId: string, since: number) =>
    Math.floor(Number(sessionId) / MS_PER_ID_TICK) > since

export const rotateSession = (since?: number): string => {
    const stored = readStoredSessionId()

    if (since !== undefined && stored !== undefined && isMintedAfter(stored, since)) {
        writeSessionId(stored)
        fallback = undefined
        notifyRotation(stored)

        return stored
    }

    const minted = mintSessionId()

    writeSessionId(minted)
    fallback =
        readStoredSessionId() === undefined ? { id: minted, touchedAt: Date.now() } : undefined
    notifyRotation(minted)

    return minted
}

export const readSessionId = (): string => readStoredSessionId() ?? touchSession()
